import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import Image from 'next/image'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import Accordion from '../src/components/Accordion'
import EarnGuide from '../src/components/Product/rewards/EarnGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
}))

const howCryptoSavingsWork = [
  {
    summary: 'Register for free',
    details:
      'Sign-up for a wallet offering crypto saving accounts as a feature. Put your e-mail and password.',
    action: {
      label: 'Compare savings accounts',
      link: urls.savingAccounts,
    },
  },
  {
    summary: 'Verify identity',
    details:
      'Like all custodial wallets you will need to verify your identity by uploading a copy of your passport.',
  },
  {
    summary: 'Buy crypto',
    details:
      "In order to lend crypto assets to earn interest you first need to buy some. You can also buy stablecoins or dollar tokens if you don't want to be exposed to volatile crypto assets.",
  },
  {
    summary: 'Lend crypto',
    details:
      'With a simple button click, give your wallet provider the authorization to lend your crypto assets on your behalf. You will receive interest paid out weekly or monthly depending on the provider.',
  },
  {
    summary: 'Safety',
    details:
      'Borrowers who take the loans need to put up collateral to get a loan making this type of lending relatively secure for you.',
  },
]

const howDefiLendingWork = [
  {
    summary: 'Get an Ethereum wallet',
    details:
      'To use a decentralized application you need a non-custodial wallet. Your wallet is your gateway into DeFi.',
    action: {
      label: 'Compare Ethereum wallets',
      link: urls.ethereumWallets,
    },
  },
  {
    summary: 'Fund your wallet',
    details:
      'You need some Ether in your wallet to pay for transaction fees on the Blockchain. You can transfer funds from an exchange.',
  },
  {
    summary: 'Choose a DeFi lending app',
    details:
      'Find a DeFi lending protocol that you like. Choose one that offers a good yield, manages a lot of capital and has a good Cryptotesters score.',
    action: {
      label: 'Compare DeFi lending protocols',
      link: urls.defi,
    },
  },
  {
    summary: 'Deposit funds',
    details: 'Navigate to the website, connect your wallet and deposit funds into the protocol.',
  },
  {
    summary: 'Start earning interest',
    details:
      'You will automatically start earning interest every few seconds. The interest rates fluctuate based on supply & demand.',
  },
]

const howStakingWork = [
  {
    summary: 'Get an Ethereum wallet',
    details:
      'To use a decentralized staking platform you need a non-custodial wallet. Your wallet is your gateway into staking.',
    action: {
      label: 'Compare Ethereum wallets',
      link: urls.ethereumWallets,
    },
  },
  {
    summary: 'Fund your wallet',
    details:
      'You need some Ether in your wallet to pay for transaction fees on the Blockchain and to stake. You can transfer funds from an exchange into your wallet.',
  },
  {
    summary: 'Choose a staking platform',
    details:
      'Find an Ethereum staking platform that you like. Choose one that offers a good yield, manages a lot of capital and has a good Cryptotesters score.',
    action: {
      label: 'Compare staking platforms',
      link: urls.ethereumStaking,
    },
  },
  {
    summary: 'Earn Ether',
    details:
      'Once you deposit Ether your deposit will be added to a queue as it takes time to deploy a new staking node. This activation period can last between 1 day or 1 week but once it’s activated, you start earning rewards.',
  },
]

const faqs = [
  {
    question: 'How do crypto savings accounts work?',
    answer:
      'Crypto savings accounts work in a similar way to normal bank savings accounts. In a nutshell, you lend money to an institution, which lends your assets to borrowers in need of liquidity. However, these loans are relatively secure since the loan providers ask the borrowers to deposit crypto assets themselves, as security for the loan. Most providers ask for a ‘loan-to-value’ ratio of 50% meaning that if a borrower wants $1000 they’ll need to deposit $2000 worth of crypto assets as security for the loan.',
  },
  {
    question: 'Who are my funds being lent to?',
    answer:
      'Broadly speaking, the borrowers are institutions and sometimes individuals that hold cryptocurrency and need short-term liquidity but don’t want to sell their cryptocurrency. Examples of companies using crypto-backed loans are miners, venture capital funds and most importantly trading firms.',
  },
  {
    question: 'Can crypto lending platforms be hacked?',
    answer:
      'Yes, both DeFi and centralized lending platforms can theoretically be hacked. Centralized lending platforms store unser funds in centrally managed wallets which could be hacked. DeFi platforms are managed through smart contracts which could have bugs.',
  },
]

const Earn = (): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Earn`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - Earn`,
              description: `Learn how to earn interest on your crypto assets.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">Earn interest on your crypto assets</Typography>
                  <Typography variant="body2">
                    Earn interest on your crypto assets by lending or staking them. Both lending and
                    staking can be done with centralized wallets or non-custodial options.
                  </Typography>
                  {/* <Button
                    color="primary"
                    variant="contained"
                    href={urls.savingAccounts}
                    className={classes.heroCTA}
                  >
                    Compare savings plans
                  </Button> */}
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/earn/earn.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Crypto saving accounts</Typography>
                <Typography variant="body2">
                  Crypto saving accounts powered by centralized providers take your deposits and
                  lend them to vetted institutional borrowers who pay interest for the loan.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.savingAccounts}
                  className={classes.heroCTA}
                >
                  Compare savings accounts
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Crypto Savings Accounts
                </Button> */}
                <Typography variant="h3">How do crypto savings work?</Typography>
                <Accordion steps={howCryptoSavingsWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/earn/savings-account.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">DeFi lending accounts</Typography>
                <Typography variant="body2">
                  DeFi lending protocols let you lend your crypto assets and earn interest on the
                  Blockchain - available to anyone in the world without identity verification or
                  paperwork.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.defi}
                  className={classes.heroCTA}
                >
                  Compare DeFi lending protocols
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: what is DeFi lending?
                </Button> */}
                <Typography variant="h3">How do DeFi lending rates work?</Typography>
                <Accordion steps={howDefiLendingWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/earn/defi-lending.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Ethereum staking platforms</Typography>
                <Typography variant="body2">
                  Ethereum staking platforms let you participate in the transaction validation
                  process of the Ethereum network - called Proof-of-Stake. When you stake your Ether
                  (ETH) you earn rewards paid in ETH.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.ethereumStaking}
                  className={classes.heroCTA}
                >
                  Compare staking protocols
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: stakings
                </Button> */}
                <Typography variant="h3">How does Ethereum staking work?</Typography>
                <Accordion steps={howStakingWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/earn/eth-staking.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <div>
              <EarnGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export default Earn
